import { useCallback, useState } from 'react';
import { requestCouponDownloadAll } from '@apis/renewal/coupon';
import { getCouponMessage } from '@wconcept/wconcept-common/utils/coupon';
import {
  addAsIsMWebDomain,
  currentUrl,
} from '@wconcept/wconcept-common/utils/domainUtil';
import useGlobalState from '@hooks/useGlobalState';
import useModalManager from '@hooks/useModalManager';

interface CouponDownloadOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  showAlert?: boolean;
}

interface CouponDownloadState {
  isLoading: boolean;
  error: string | null;
}

export const useCouponDownload = () => {
  const { assistData } = useGlobalState();
  const { openLoginConfirm } = useModalManager();
  const [state, setState] = useState<CouponDownloadState>({
    isLoading: false,
    error: null,
  });

  // 로그인 체크 및 리다이렉트
  const checkLoginAndRedirect = useCallback(async (): Promise<boolean> => {
    if (assistData?.user?.userNo === '0' && !assistData?.isLogin) {
      openLoginConfirm();
      return false;
    }
    return assistData?.isLogin && assistData?.user?.userNo !== '0';
  }, [assistData?.isLogin, assistData?.user?.userNo, openLoginConfirm]);

  // 쿠폰 다운로드 실행
  const downloadCoupons = useCallback(
    async (
      couponCds: string[],
      options: CouponDownloadOptions = {}
    ): Promise<boolean> => {
      const { onSuccess, onError, showAlert = true } = options;

      if (couponCds.length === 0) {
        const errorMsg = '다운로드할 쿠폰이 없습니다.';
        setState(prev => ({ ...prev, error: errorMsg }));
        onError?.(errorMsg);
        if (showAlert) {
          window.alert(errorMsg);
        }
        return false;
      }

      const isLoggedIn = await checkLoginAndRedirect();
      if (!isLoggedIn) {
        return false;
      }

      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await requestCouponDownloadAll({ couponCds });

        // API 응답 구조에 따라 성공 여부 판단
        const isSuccess = response?.couponDownlodInfos?.resultCode === 0;
        const message = getCouponMessage(
          isSuccess,
          response?.couponDownlodInfos?.resultMessage
        );

        setState(prev => ({ ...prev, isLoading: false }));

        if (isSuccess) {
          onSuccess?.(message);
        } else {
          setState(prev => ({ ...prev, error: message }));
          onError?.(message);
        }

        if (showAlert) {
          window.alert(message);
        }

        return isSuccess;
      } catch (error) {
        const errorMsg =
          '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        setState(prev => ({ ...prev, isLoading: false, error: errorMsg }));
        onError?.(errorMsg);

        if (showAlert) {
          window.alert(errorMsg);
        }

        console.error('쿠폰 다운로드 실패:', error);
        return false;
      }
    },
    [checkLoginAndRedirect]
  );

  // 단일 쿠폰 다운로드
  const downloadSingleCoupon = useCallback(
    async (couponCd: string, options?: CouponDownloadOptions) =>
      downloadCoupons([couponCd], options),
    [downloadCoupons]
  );

  // 여러 쿠폰 다운로드
  const downloadMultipleCoupons = useCallback(
    async (couponCds: string[], options?: CouponDownloadOptions) =>
      downloadCoupons(couponCds, options),
    [downloadCoupons]
  );

  // 에러 클리어
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    downloadSingleCoupon,
    downloadMultipleCoupons,
    downloadCoupons,
    clearError,
    isLoggedIn: assistData?.isLogin && assistData?.user?.userNo !== '0',
  };
};
